import React, { useEffect, useRef } from "react";
import { useApplePayLoader } from "hooks/useApplePayLoader";
import classes from "./ApplePay.module.scss";
import { useRequest } from "hooks";
import { FT_EWALLET_APPLEPAY } from "constants/request";
import { useRootSelector } from "store/hooks";
import { PAYMENT_METHOD_TYPE } from "utility/utils";
import { ApplePayload } from "store/payments/payments-state";

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "apple-pay-button": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        buttonstyle?: string;
        type?: string;
        locale?: string;
      };
    }
  }
}

interface ApplePayProps {
  amount?: number;
  onTokenReceived: (payload: ApplePayload) => void;
}

export const ApplePay: React.FC<ApplePayProps> = ({
  amount,
  onTokenReceived,
}) => {
  const { loaded, error, latestVersion, isApplePaySupported } =
    useApplePayLoader();

  const buttonRef = useRef<HTMLElement>(null);
  const { request } = useRequest();
  const { client: clientConfig, ewallet: ewalletConfig } = useRootSelector(
    (state) => state.config,
  );

  useEffect(() => {
    const buttonEl = buttonRef.current;
    if (!buttonEl) return;
    const handler = () => handleApplePayClick();
    buttonEl.addEventListener("click", handler);
    return () => {
      buttonEl.removeEventListener("click", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, amount]);

  const handleApplePayClick = () => {
    if (
      !window.ApplePaySession ||
      !window.ApplePaySession.canMakePayments() ||
      !isApplePaySupported ||
      !latestVersion
    ) {
      alert("Apple Pay is not supported in this browser.");
      return;
    }

    const paymentRequest = {
      countryCode: "US",
      currencyCode: "USD",
      supportedNetworks: ["visa", "masterCard", "amex", "discover", "jcb"],
      merchantCapabilities: ["supports3DS"],
      merchantIdentifier:
        ewalletConfig.applePayConfiguration?.merchantIdentifier || "",
      total: { label: clientConfig.name, amount: amount ?? 0, type: "final" },
    };

    const session = new window.ApplePaySession(latestVersion, paymentRequest);

    session.onvalidatemerchant = async (event: any) => {
      try {
        const response = await request(FT_EWALLET_APPLEPAY, {
          method: "POST",
          headers: { ContentType: "application/json" },
          data: { clientId: clientConfig.clientId },
        });

        const merchantSession = response.data ?? response;
        session.completeMerchantValidation(merchantSession);
      } catch (error) {
        console.error("Error validating merchant:", error);
        session.abort();
      }
    };

    session.onpaymentauthorized = async (event: any) => {
      const appleToken = event.payment.token;
      onTokenReceived({
        token: appleToken,
        walletType: 2,
        paymentMethodType: PAYMENT_METHOD_TYPE.eWallet,
      });
      session.completePayment({
        status: window.ApplePaySession.STATUS_SUCCESS,
        errors: [],
      });
    };

    session.oncancel = () => {
      console.log("Apple Pay session was cancelled.");
    };

    session.begin();
  };

  if (error)
    return <div className={classes.Error}>Apple Pay SDK failed to load.</div>;
  if (!isApplePaySupported) return null;
  if (!loaded || latestVersion === null)
    return <div className={classes.Loading}>Loading Apple Pay...</div>;

  return (
    <apple-pay-button
      ref={buttonRef}
      buttonstyle="black"
      type="plain"
      locale="en-US"
      className={classes.ApplePayButton}
    ></apple-pay-button>
  );
};
